import React, { useState, useEffect } from 'react';
import { HeroSection } from '@/components/landing/HeroSection';
import { RideInventorySections, TrustedBySection } from '@/components/landing/RideInventorySections';
import { AppStoreButtons } from '@/components/landing/AppStoreButtons';
import { QuickRideModal } from '@/components/landing/QuickRideModal';
import { languageUtils, SupportedLanguage, isLanguageSelectionEnabled } from '@/utils/languageUtils';
import Cookies from 'js-cookie';


// Consent Dialog Component
const LocationConsentDialog: React.FC<{ open: boolean; onAccept: () => void; onDecline: () => void }> = ({ open, onAccept, onDecline }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-lg p-6 max-w-sm w-full">
        <h2 className="text-lg font-semibold mb-2">Personalized Language Experience</h2>
        <p className="mb-4 text-gray-700 text-sm">
          We can personalize your experience by showing content in your local language based on your state (using your IP address). No precise location or personal data is collected. Do you allow us to detect your state for this purpose?
        </p>
        <div className="flex justify-end gap-2">
          <button className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300" onClick={onDecline}>No, thanks</button>
          <button className="px-4 py-2 rounded bg-[#22C55E] text-white hover:bg-[#16A34A]" onClick={onAccept}>Allow</button>
        </div>
      </div>
    </div>
  );
};

const Landing = ({
  showAuthModal,
  setShowAuthModal,
  authMode,
  setAuthMode,
  showQuickRideModal,
  setShowQuickRideModal,
  selectedCountry,
  setSelectedCountry
}: {
  showAuthModal: boolean;
  setShowAuthModal: React.Dispatch<React.SetStateAction<boolean>>;
  authMode: 'login' | 'register';
  setAuthMode: React.Dispatch<React.SetStateAction<'login' | 'register'>>;
  showQuickRideModal: boolean;
  setShowQuickRideModal: React.Dispatch<React.SetStateAction<boolean>>;
  selectedCountry?: string;
  setSelectedCountry?: React.Dispatch<React.SetStateAction<string>>;
}) => {
  const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage>(languageUtils.getCurrentLanguage());
  const [showConsent, setShowConsent] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);

  // On mount: country cookie only. Language is English-only for now.
  useEffect(() => {
    const savedCountry = Cookies.get('country');
    if (savedCountry && setSelectedCountry) {
      setSelectedCountry(savedCountry);
    }

    // Language localization consent is disabled until multi-language ships.
    if (!isLanguageSelectionEnabled) {
      setShowConsent(false);
      return;
    }

    const savedLanguage = Cookies.get('chalo_language');
    if (savedLanguage) {
      setSelectedLanguage(savedLanguage as SupportedLanguage);
    }

    const urlParams = new URLSearchParams(window.location.search);
    const utmCampaign = urlParams.get('utm_campaign');
    const isDownloadAppCampaign = utmCampaign === 'downloadapp';

    if (!savedLanguage && !isDownloadAppCampaign) {
      setShowConsent(true);
    }
  }, []);

  // Handle consent accept: fetch state from IP and set language
  // Location fetching removed to prevent browser permission popup
  const handleConsentAccept = async () => {
    setShowConsent(false);
    setConsentChecked(true);
    // Location-based language detection disabled
    languageUtils.setLanguage('en');
    setSelectedLanguage('en');
    setSelectedCountry?.('OTHER');
    Cookies.set('country', 'OTHER', { expires: 365, sameSite: 'Strict' });
    Cookies.set('chalo_language', 'en', { expires: 365, sameSite: 'Strict' });
    // try {
    //   const { lat, lng } = await getBrowserLocation({ timeout: 10000 });
    //   const { city, state, country } = await getCityStateFromLatLng(lat, lng);
    //   // If country is not supported, fallback to 'OTHER' and English
    //   let detectedCountry = country;
    //   let detectedLang: SupportedLanguage = 'en';
    //   if (!COUNTRY_LANGUAGES_MAP[detectedCountry]) {
    //     detectedCountry = 'OTHER';
    //     detectedLang = 'en';
    //   } else if (state && STATE_LANGUAGE_MAP[detectedCountry] && STATE_LANGUAGE_MAP[detectedCountry][state]) {
    //     detectedLang = STATE_LANGUAGE_MAP[detectedCountry][state];
    //   } else if (COUNTRY_LANGUAGES_MAP[detectedCountry]) {
    //     detectedLang = COUNTRY_LANGUAGES_MAP[detectedCountry][0] as SupportedLanguage;
    //   }
    //   Cookies.set('country', detectedCountry, { expires: 365, sameSite: 'Strict' });
    //   Cookies.set('chalo_language', detectedLang, { expires: 365, sameSite: 'Strict' });
    //   languageUtils.setLanguage(detectedLang);
    //   setSelectedLanguage(detectedLang);
    //   setSelectedCountry?.(detectedCountry);
    // } catch (err) {
    //   languageUtils.setLanguage('en');
    //   setSelectedLanguage('en');
    //   setSelectedCountry?.('OTHER'); // Set to 'OTHER' on error
    //   Cookies.set('country', 'OTHER', { expires: 365, sameSite: 'Strict' });
    //   Cookies.set('chalo_language', 'en', { expires: 365, sameSite: 'Strict' });
    // }
  };

  // Handle consent decline: fallback to English
  const handleConsentDecline = () => {
    setShowConsent(false);
    setConsentChecked(true);
    languageUtils.setLanguage('en');
    setSelectedLanguage('en');
  };

  const handleAuthClick = (mode: 'login' | 'register') => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  const handleQuickRideClick = () => {
    setShowQuickRideModal(true);
  };

  return (
    <div className="min-h-screen bg-background-cream font-roboto">
      <LocationConsentDialog open={showConsent} onAccept={handleConsentAccept} onDecline={handleConsentDecline} />
      <main>
        <AppStoreButtons />
        <HeroSection onQuickRideClick={handleQuickRideClick} />
        <TrustedBySection />
        <RideInventorySections />
      </main>

      {/* Floating AI Assistant - only in development */}
      {/* {!IS_PRODUCTION_READY && <FloatingAIAssistant activeRole="traveller" />} */}
      
      {showQuickRideModal && (
        <QuickRideModal 
          onClose={() => setShowQuickRideModal(false)}
          onAuthRequired={() => {
            setShowQuickRideModal(false);
            setShowAuthModal(true);
          }}
        />
      )}
    </div>
  );
};

export default Landing;
